import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, ProsCons, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function LadeboksV2GReady({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    { question: 'Hvad betyder "V2G-ready" på en ladeboks?', answer: 'V2G-ready betyder, at ladeboksen er forberedt til tovejsopladning (Vehicle-to-Grid), så din elbil på sigt kan sende strøm tilbage til nettet. Hardwaren understøtter det – det mangler blot, at dansk lovgivning og elselskabsaftaler kommer på plads (ventes 2026–2027). Du fremtidssikrer dig uden ekstra omkostning.' },
    { question: 'Hvilke V2G-ready ladebokse kan jeg købe hos enkel?', answer: 'Vi tilbyder Zaptec Go 2 (5.999 kr.), der er AC-baseret V2G-klar og den billigste V2G-løsning til hjemmet, samt NexBlue Edge 2 (3.499 kr.), der er V2X-ready. Begge inkluderer Saving Mode og serviceaftale til 0 kr./md.' },
    { question: 'Skal jeg vente med at købe ladeboks, til V2G er klar i Danmark?', answer: 'Nej. Det koster ikke ekstra at vælge en V2G-forberedt boks i dag. Du får alle de almindelige fordele (22 kW, Saving Mode, laderabat) med det samme – og er klar til V2G den dag lovgivningen falder på plads. At købe en V2G-ready boks nu er derfor billig fremtidssikring.' },
    { question: 'Kan en V2G-ladeboks bruges med min nuværende bil?', answer: 'Ja, til almindelig opladning fungerer den med alle elbiler via Type 2-stik. For selve V2G-funktionen skal også bilen understøtte tovejsopladning. Mange nyere modeller (Kia EV9, kommende VW ID, Ford Capri/Explorer) understøtter eller planlægger V2G; Tesla gør endnu ikke.' },
    { question: 'Hvad er forskellen på AC- og DC-baseret V2G?', answer: 'AC-baseret V2G (fx Zaptec Go 2) er den billigste løsning til private hjem og koster nogle få tusinde kroner. DC-baseret V2G (fx Wallbox Quasar 2) er kraftigere men dyrere – ca. 25.000 kr. – og er primært til erhverv og dedikerede installationer.' },
    { question: 'Er Saving Mode og serviceaftale inkluderet med V2G-ladebokse?', answer: 'Ja. Når du har en el-aftale hos enkel energi, er Saving Mode, serviceaftale og 10 øre/kWh laderabat inkluderet til 0 kr./md. – uanset om du vælger Zaptec Go 2 eller NexBlue Edge 2.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'modeller', label: 'Vores V2G-ready bokse' },
    { id: 'sammenlign', label: 'Sammenligning' },
    { id: 'acdc', label: 'AC vs DC' },
    { id: 'fremtid', label: 'Fremtidssikring' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Ladebokse', path: '/opladere' }, { name: 'V2G-ready ladebokse', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Ladeboks med V2G 2026" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Ladebokse · V2G-ready · 2026"
        h1="Ladeboks med V2G: De bedste V2G-ready bokse i 2026"
        intro="Vil du fremtidssikre din opladning? Her er de V2G-ready ladebokse, du kan købe i dag, så din elbil senere kan sende strøm tilbage til nettet. Saving Mode og serviceaftale er inkluderet."
        badges={['Fremtidssikret', 'Saving Mode inkl.', 'Fra 3.499 kr.']}
        updated={updated}
        image="https://images.unsplash.com/photo-1647500666543-2f5f1e7e0b6e?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '3.499 kr.', label: 'NexBlue Edge 2 (V2X-ready)' },
          { value: '5.999 kr.', label: 'Zaptec Go 2 (V2G-ready)' },
          { value: '0 kr./md.', label: 'Saving Mode + service' },
          { value: '2026–2027', label: 'forventet V2G i Danmark' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Hvad er en V2G-ready ladeboks?">
        <AnswerBox question="Hvad betyder V2G-ready?">
          En <strong>V2G-ready</strong> ladeboks er forberedt til tovejsopladning, så din elbil på sigt kan <strong>sende strøm tilbage til elnettet</strong>.
          Hardwaren kan det allerede – det mangler blot, at dansk lovgivning og elselskabsaftaler falder på plads (ventes 2026–2027). Hos enkel
          energi er <strong>Zaptec Go 2</strong> den billigste V2G-klare boks til hjemmet, og <strong>NexBlue Edge 2</strong> er V2X-ready til en lavere pris.
        </AnswerBox>
        <p>
          At købe en V2G-ready boks nu er billig fremtidssikring: du betaler ikke ekstra for forberedelsen, du får alle de almindelige fordele
          (22 kW, Saving Mode, laderabat) fra dag ét, og du er klar den dag, V2G-aftaler bliver tilgængelige for private i Danmark.
        </p>
      </ProseSection>

      <ProseSection id="modeller" heading="Vores V2G-ready ladebokse" alt>
        <div className="grid sm:grid-cols-2 gap-4 my-2">
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go_2} note="V2G-ready · billigste AC-løsning" />
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.nexblue_edge_2} note="V2X-ready · bedste pris" />
        </div>
        <p>Begge er Saving Mode-kompatible og leveres med serviceaftale til 0 kr./md. og laderabat på 10 øre/kWh, når du har en el-aftale hos enkel energi.</p>
      </ProseSection>

      <ProseSection id="sammenlign" heading="Sammenligning af V2G-ready bokse">
        <div className="my-2">
          <ComparisonTable
            caption="Zaptec Go 2 vs NexBlue Edge 2"
            columns={[{ name: 'Zaptec Go 2', highlight: true }, { name: 'NexBlue Edge 2' }]}
            rows={[
              { label: 'Pris', values: ['5.999 kr.', '3.499 kr.'] },
              { label: 'Tovejs-status', values: ['V2G-ready (AC)', 'V2X-ready'] },
              { label: 'Ladekraft', values: ['22 kW', '22 kW'] },
              { label: 'Forbindelse', values: ['Wi-Fi + 4G', 'Wi-Fi'] },
              { label: 'Skærm', values: ['Indbygget', 'LED status-ring'] },
              { label: 'Garanti', values: ['5 år', 'Producentgaranti'] },
              { label: 'Saving Mode', values: [true, true] },
              { label: 'Serviceaftale', values: ['0 kr./md.', '0 kr./md.'] },
            ]}
          />
        </div>
      </ProseSection>

      <ProseSection id="acdc" heading="AC- eller DC-baseret V2G?" alt>
        <ProsCons
          prosTitle="AC-baseret (fx Zaptec Go 2)"
          consTitle="DC-baseret (fx Wallbox Quasar 2)"
          pros={['Billig – nogle få tusinde kroner', 'Perfekt til private hjem', 'Kompakt og enkel installation', 'Saving Mode + serviceaftale inkluderet hos enkel']}
          cons={['Dyr – ca. 25.000 kr.', 'Primært til erhverv', 'Kræver mere plads og installation', 'Overkill for de fleste private']}
        />
        <p className="mt-4">For langt de fleste danske husstande er en AC-baseret V2G-ready boks som Zaptec Go 2 det rette – og det mest økonomiske – valg.</p>
      </ProseSection>

      <ProseSection id="fremtid" heading="Hvornår kan du bruge V2G?">
        <FactGrid columns={3} facts={[
          { value: 'Sverige', label: 'Europas største V2G-projekt', sub: '200 tovejsladere kører nu' },
          { value: 'Q4 2026', label: 'VW ruller V2G ud i Tyskland', sub: 'Pilot i Sverige' },
          { value: '2026–2027', label: 'Forventet i Danmark', sub: 'Rammer på plads i Energistyrelsen' },
        ]} />
        <p className="mt-6">
          De juridiske rammer halter lidt efter teknologien, men Danmark forventes typisk at følge Sverige og Tyskland inden for 1–2 år.
          Køber du en V2G-forberedt boks nu, er du klar den dag, lovgivningen er på plads – uden at have betalt ekstra for det.
          Læs mere i vores <a href="/sammenlign/v2g-guide" className="text-[#0f5c4a] font-semibold underline">store V2G-guide</a>.
        </p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/sammenlign/v2g-guide', label: 'Stor V2G-guide' },
        { path: '/zaptec-go-2', label: 'Zaptec Go 2' },
        { path: '/nexblue-edge-2', label: 'NexBlue Edge 2' },
        { path: '/sammenlign/zaptec-go-vs-go-2', label: 'Zaptec Go vs Go 2' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Fremtidssikr din opladning" subtext="Vælg en V2G-ready boks i dag – klar til at sælge strøm tilbage, når Danmark er det." primary={{ path: '/opladere', text: 'Se ladebokse' }} secondary={{ path: '/sammenlign/v2g-guide', text: 'Læs V2G-guiden' }} />
      <SEODisclaimer page={page} />
    </article>
  );
}